import { Card, CardBody, CardTitle } from 'reactstrap';
import cls from 'clsx';
import { useMutation } from '@tanstack/react-query';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faHardHat } from '@fortawesome/free-solid-svg-icons';
import { useFormValues, useSetFieldValue } from '@s20/components/forms/FormContext';
import { httpClient } from '@s20/services/httpClient';
import {
    ActivityIcons, ApiRoutes, EventStatus,
} from '../../../../../Common';
import { Flex, IconButton } from '../../../../../common/forms/FormElements';
import { ConfirmationStatus, DraggableResourceTypes } from '../../../../../dispatch/Dispatch';
import { CancelJobAssignment } from '../CancelJobAssignment';
import { ReplaceJobAssignment } from '../ReplaceJobAssignment';
import { useUpdateAssignment } from '../useUpdateAssignment.hook';
import { getStatusName } from './common';

export const Employee = ({
    crewLead, dispatchLocationId, canEdit, ...group
}) => {
    const setFieldValue = useSetFieldValue();
    const selectedResourceGroup = useFormValues('selectedResourceGroup');

    const hasAssignment = !!group.assignment;

    const mutationHandlers = useUpdateAssignment({
        jobId: group.jobId,
        jobAssignmentId: group.jobAssignmentId,
        onMutate: (employee, isMatch) => ({
            ...employee,
            isCrewLead: isMatch,
        }),
    });

    const handleSetCrewLead = useMutation({
        mutationFn: () => httpClient.js(ApiRoutes.dispatch.setCrewLead(group.jobAssignmentId)),
        ...mutationHandlers,
    });

    if (hasAssignment) {
        return (
            <Card
                className={cls(
                    'rounded-lg',
                    getStatusName(group.assignment),
                )}
            >
                <CardBody className="p-2">
                    <CardTitle className="text-left d-flex flex-row justify-content-between align-items-center mb-0">
                        <span className="d-flex flex-row flex-fill justify-content-between align-items-center">
                            <span>
                                {`${group.name}`}

                                {group.employeeDispatchOffice && group.employeeDispatchOffice.id !== dispatchLocationId && (
                                    <span className="d-block">Dispatched from {group.employeeDispatchOffice.name}</span>
                                )}

                                {group.lastEvent && (
                                    <span
                                        className={`d-block mt-1 ${group.lastEvent === EventStatus.NoShow ? 'text-danger' : 'assignment-status-label confirmed'
                                            }`}
                                    >
                                        <FontAwesomeIcon
                                            icon={ActivityIcons[group.lastEvent]}
                                            className="mr-1"
                                        />
                                        <span>{group.lastEvent}</span>
                                    </span>
                                )}
                            </span>

                            <div className="d-flex flex-column">
                                <div className="d-flex flex-row justify-content-end align-items-center">
                                    <IconButton
                                        color="link"
                                        size="sm"
                                        title={
                                            group.isCrewLead
                                                ? crewLead
                                                : `Mark this employee as the ${crewLead}`
                                        }
                                        disabled={!canEdit || !!group.isCrewLead}
                                        className="mr-1"
                                        onClick={handleSetCrewLead.mutateAsync}
                                    >
                                        <FontAwesomeIcon
                                            icon={faHardHat}
                                            className={cls(
                                                'crewLeadBadge',
                                                {
                                                    isCrewLead:
                                                        !!group.isCrewLead,
                                                },
                                            )}
                                        />
                                    </IconButton>

                                    <span className="text-overflow-ellipsis mr-2">
                                        {group.assignment.name}
                                    </span>
                                </div>
                                <span
                                    className={`text-right assignment-status-label mr-2 ${group.assignment.confirmationStatusName
                                        .replace(
                                            ' ',
                                            '',
                                        )
                                        .toLowerCase()}`}
                                >
                                    {group.assignment.confirmationStatusName}
                                </span>
                            </div>
                        </span>
                        {
                            [ConfirmationStatus.Unfilled, ConfirmationStatus.Scheduled]
                                .includes(group.assignment.confirmationStatusId) && canEdit && (
                                <ReplaceJobAssignment
                                    jobAssignmentId={group.jobAssignmentId}
                                    resourceType={DraggableResourceTypes.Staff}
                                />
                            )
                        }
                        {
                            // job.canEdit is backed by whether or not the crew has submitted a timesheet.
                            // if we have a timesheet for this crew for this day, we cannot remove the assignment.
                            group.assignment.confirmationStatusId !== ConfirmationStatus.CancelPending && canEdit && (
                                <CancelJobAssignment
                                    jobAssignmentId={group.jobAssignmentId}
                                    resourceType={DraggableResourceTypes.Staff}
                                />
                            )
                        }
                    </CardTitle>
                </CardBody>
            </Card>
        );
    }

    return (
        <Card
            className={cls('empty-assignment rounded-lg', selectedResourceGroup?.id === group.id && 'pulse')}
            tag="button"
            type="button"
            onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();

                setFieldValue('selectedResourceType', DraggableResourceTypes.Staff);
                setFieldValue('selectedResourceGroup', group);
            }}
        >
            <CardBody className="p-2">
                <CardTitle className="mb-0">
                    <Flex
                        justifyContent="center"
                        alignItems="center"
                    >
                        {group.name}
                    </Flex>
                </CardTitle>
            </CardBody>
        </Card>
    );
};